// client/src/components/categorias/CategoriaForm.js - VERSÃO COMPLETA E CORRIGIDA
import React, { useState, useEffect, useCallback } from 'react';
import { useMutateCategorias, useCategoriasParaFormulario } from '../../hooks/useCategorias';
import useAlert from '../../hooks/useAlert';
import { XCircle, Loader2, Upload, Trash2, Tag, ListTree } from 'lucide-react';
import { useAuth } from '../../context/auth/AuthProvider';
import { FormStepper, FormStepNavigation } from '../shared/FormStepper';

const STEP_LABELS = ['Dados Básicos', 'Hierarquia', 'Imagem', 'Tags', 'Configurações'];

const initialState = {
    nome: '',
    descricao: '',
    tipo: 'servico',
    ordem: 0,
    ativo: true,
    visivelParaCliente: true,
    categoriaPai: '',
    metadata: {
        tags: []
    }
};

const CategoriaForm = ({ onCloseForm, onSaveSuccess, current, showHeader = true }) => {
    const { user } = useAuth();
    const { setAlert } = useAlert();
    
    // ✅ CORREÇÃO: Usar o hook de mutação corretamente
    const { 
        createCategoria, 
        updateCategoria, 
        uploadImagemCategoria, 
        removeImagemCategoria,
        isLoading 
    } = useMutateCategorias(user?.account?._id);
    
    const [formData, setFormData] = useState(initialState);
    const [currentStep, setCurrentStep] = useState(1);

    const { 
        data: categoriasFormulario, 
        isLoading: isLoadingCategorias,
        error: errorCategorias 
    } = useCategoriasParaFormulario(user?.account?._id, formData.tipo);
    
    const [imagemPreview, setImagemPreview] = useState(null);
    const [imagemFile, setImagemFile] = useState(null);
    const [novaTag, setNovaTag] = useState('');
    
    const isEditing = !!current;
    const loading = isLoading.create || isLoading.update;
    const uploadLoading = isLoading.uploadImage || isLoading.removeImage;

    const imagemUrl = current?.imagemUrl;

    useEffect(() => {
        console.log('🔍 [CATEGORIA_FORM] Categorias carregadas:', {
            total: categoriasFormulario?.length || 0,
            categorias: categoriasFormulario,
            isLoading: isLoadingCategorias,
            error: errorCategorias,
            tipoFiltro: formData.tipo
        });
    }, [categoriasFormulario, isLoadingCategorias, errorCategorias, formData.tipo]);

    const categoriasDisponiveis = categoriasFormulario?.filter(cat => {
        const categoriaId = cat._id || cat.id;
        
        if (!cat || !categoriaId) {
            console.log('❌ [FILTRO] Categoria inválida:', cat);
            return false;
        }
        
        const currentId = current?._id || current?.id;
        if (isEditing && categoriaId === currentId) {
            console.log('❌ [FILTRO] Não pode ser pai de si mesma:', cat.nome);
            return false;
        }
        
        if (!cat.ativo) {
            console.log('❌ [FILTRO] Categoria inativa:', cat.nome);
            return false;
        }
        
        console.log('✅ [FILTRO] Categoria disponível:', cat.nome, cat.tipo, 'ID:', categoriaId);
        return true;
    }) || [];

    useEffect(() => {
        console.log('🔍 [CATEGORIA_FORM] Categorias disponíveis para pai:', {
            totalDisponiveis: categoriasDisponiveis.length,
            categoriasDisponiveis: categoriasDisponiveis,
            tipoAtual: formData.tipo,
            todasCategorias: categoriasFormulario
        });
    }, [categoriasDisponiveis, formData.tipo, categoriasFormulario]);

    useEffect(() => {
        const testarCategorias = async () => {
            try {
                const api = (await import('../../services/api')).default;
                const response = await api.get('/categorias', {
                    params: {
                        account: user?.account?._id,
                        ativo: 'true',
                        limit: 500,
                        populate: 'categoriaPai',
                        tipo: formData.tipo
                    }
                });
                console.log('🔍 [TESTE_DIRETO] Resposta da API:', response.data);
                console.log('📝 [TESTE_DIRETO] Primeira categoria:', response.data.data?.[0]);
            } catch (error) {
                console.error('❌ [TESTE_DIRETO] Erro:', error);
            }
        };
        
        if (user?.account?._id && !categoriasFormulario?.length) {
            testarCategorias();
        }
    }, [user?.account?._id, categoriasFormulario, formData.tipo]);

    useEffect(() => {
        if (isEditing && current) {
            const currentId = current._id || current.id;
            const categoriaPaiId = current.categoriaPai?._id || current.categoriaPai?.id || current.categoriaPai || '';
            
            setFormData({
                nome: current.nome || '',
                descricao: current.descricao || '',
                tipo: current.tipo || 'servico',
                ordem: current.ordem || 0,
                ativo: current.ativo !== undefined ? current.ativo : true,
                visivelParaCliente: current.visivelParaCliente !== undefined ? current.visivelParaCliente : true,
                categoriaPai: categoriaPaiId,
                metadata: {
                    tags: current.metadata?.tags || []
                }
            });
            
            if (imagemUrl) {
                setImagemPreview(imagemUrl);
            }
        } else {
            setFormData(initialState);
            setImagemPreview(null);
            setImagemFile(null);
            setNovaTag('');
        }
    }, [current, isEditing, imagemUrl]);

    const onChange = useCallback(e => {
        const { name, value, type, checked } = e.target;
        
        if (name.startsWith('metadata.')) {
            const field = name.split('.')[1];
            setFormData(prev => ({
                ...prev,
                metadata: {
                    ...prev.metadata,
                    [field]: type === 'checkbox' ? checked : value
                }
            }));
        } else {
            setFormData(prev => ({ 
                ...prev, 
                [name]: type === 'checkbox' ? checked : value 
            }));
        }
    }, []);

    const adicionarTag = () => {
        if (!novaTag.trim()) return;
        
        const tagFormatada = novaTag.trim().toLowerCase();
        
        if (!formData.metadata.tags.includes(tagFormatada)) {
            setFormData(prev => ({
                ...prev,
                metadata: {
                    ...prev.metadata,
                    tags: [...prev.metadata.tags, tagFormatada]
                }
            }));
        }
        
        setNovaTag('');
    };

    const removerTag = (tagParaRemover) => {
        setFormData(prev => ({
            ...prev,
            metadata: {
                ...prev.metadata,
                tags: prev.metadata.tags.filter(tag => tag !== tagParaRemover)
            }
        }));
    };

    const handleImagemChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (!file.type.startsWith('image/')) {
                setAlert('Por favor, selecione apenas arquivos de imagem.', 'warning');
                return;
            }
            
            if (file.size > 5 * 1024 * 1024) {
                setAlert('A imagem deve ter no máximo 5MB.', 'warning');
                return;
            }
            
            setImagemFile(file);
            
            const reader = new FileReader();
            reader.onload = (e) => {
                setImagemPreview(e.target.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleRemoverImagem = async () => {
        if (!isEditing || !current) return;
        
        const currentId = current._id || current.id;
        if (!currentId) return;
        
        try {
            await removeImagemCategoria.mutateAsync(currentId);
            setImagemPreview(null);
            setImagemFile(null);
            setAlert('Imagem removida com sucesso!', 'success');
        } catch (error) {
            console.error('❌ [REMOVER_IMAGEM] Erro:', error);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            adicionarTag();
        }
    };

    const getNomeFormatado = (categoria) => {
        let nome = categoria.nome || 'Sem nome';
        const tipo = categoria.tipo || 'geral';
        
        if (categoria.categoriaPai) {
            return `${nome} ↳ Subcategoria (${tipo})`;
        }
        
        return `${nome} (${tipo})`;
    };

    const getCategoriaId = (categoria) => {
        return categoria._id || categoria.id;
    };

    const onSubmit = async e => {
        e.preventDefault();
        
        if (!formData.nome || !formData.nome.trim()) {
            return setAlert('O campo Nome é obrigatório.', 'warning');
        }

        const dataToSubmit = {
            nome: formData.nome.trim(),
            descricao: formData.descricao.trim(),
            tipo: formData.tipo,
            ordem: parseInt(formData.ordem) || 0,
            ativo: formData.ativo,
            visivelParaCliente: formData.visivelParaCliente,
            categoriaPai: formData.categoriaPai || undefined,
            metadata: {
                tags: formData.metadata.tags,
                ultimaAtualizacao: new Date()
            }
        };

        try {
            let categoriaSalva;
            const currentId = current?._id || current?.id;
            
            if (isEditing && currentId) {
                categoriaSalva = await updateCategoria.mutateAsync({ 
                    id: currentId, 
                    data: dataToSubmit 
                });
            } else {
                categoriaSalva = await createCategoria.mutateAsync(dataToSubmit);
            }

            const categoriaSalvaId = categoriaSalva?.data?._id || categoriaSalva?.data?.id;
            if (imagemFile && categoriaSalvaId) {
                await uploadImagemCategoria.mutateAsync({
                    categoriaId: categoriaSalvaId,
                    imagem: imagemFile
                });
            }

            setAlert(
                isEditing ? 'Categoria atualizada com sucesso!' : 'Categoria criada com sucesso!', 
                'success'
            );
            onSaveSuccess();
        } catch (error) {
            console.error('❌ [SUBMIT_CATEGORIA] Erro:', error);
        }
    };

    return (
        <form onSubmit={onSubmit} noValidate className="max-h-[80vh] overflow-y-auto">
            {showHeader && (
                <div className="form-header sticky top-0 bg-white z-10">
                    <h3 className="form-title">
                        {isEditing ? `Editar Categoria: ${formData.nome || '...'}` : 'Nova Categoria'}
                    </h3>
                    <button 
                        type="button" 
                        onClick={onCloseForm} 
                        title="Fechar Formulário" 
                        className="form-close-button"
                    >
                        <XCircle size={24} />
                    </button>
                </div>
            )}

            <FormStepper currentStep={currentStep} steps={STEP_LABELS} />

            {currentStep === 1 && (
            <div className="form-grid-container">
                <div className="form-group col-span-2">
                    <label htmlFor="nome" className="required">Nome da Categoria</label>
                    <input 
                        type="text" 
                        name="nome" 
                        id="nome" 
                        value={formData.nome} 
                        onChange={onChange} 
                        required 
                        className="form-input" 
                        placeholder="Digite o nome da categoria"
                        maxLength="100"
                    />
                    <div className="form-hint">
                        {formData.nome.length}/100 caracteres
                    </div>
                </div>

                <div className="form-group">
                    <label htmlFor="tipo" className="required">Tipo</label>
                    <select 
                        name="tipo" 
                        id="tipo" 
                        value={formData.tipo} 
                        onChange={onChange} 
                        required 
                        className="form-input"
                        disabled={isEditing}
                    >
                        <option value="servico">Serviço</option>
                        <option value="produto">Produto</option>
                        <option value="recurso">Recurso</option>
                        <option value="curso">Curso</option>
                        <option value="geral">Geral</option>
                    </select>
                </div>

                <div className="form-group">
                    <label htmlFor="ordem">Ordem de Exibição</label>
                    <input 
                        type="number" 
                        name="ordem" 
                        id="ordem" 
                        value={formData.ordem} 
                        onChange={onChange} 
                        min="0"
                        step="1"
                        className="form-input"
                        placeholder="0"
                    />
                    <div className="form-hint">
                        Número para ordenação
                    </div>
                </div>

                <div className="form-group col-span-2">
                    <label htmlFor="descricao">Descrição</label>
                    <textarea 
                        name="descricao" 
                        id="descricao" 
                        value={formData.descricao} 
                        onChange={onChange} 
                        className="form-input" 
                        rows="3"
                        placeholder="Descrição opcional da categoria"
                        maxLength="500"
                    />
                    <div className="form-hint">
                        {formData.descricao.length}/500 caracteres
                    </div>
                </div>
            </div>
            )}

            {currentStep === 2 && (
            <div className="form-section">
                <h4 className="form-section-title">
                    <ListTree size={18} />
                    Hierarquia da Categoria
                </h4>

                <div className="form-group">
                    <label htmlFor="categoriaPai">Categoria Pai</label>
                    <select 
                        name="categoriaPai" 
                        id="categoriaPai" 
                        value={formData.categoriaPai} 
                        onChange={onChange} 
                        className="form-input"
                        disabled={isLoadingCategorias}
                    >
                        <option value="">Nenhuma (Categoria Raiz)</option>
                        
                        {errorCategorias ? (
                            <option value="" disabled className="text-red-500">
                                ❌ Erro ao carregar categorias
                            </option>
                        ) : isLoadingCategorias ? (
                            <option value="" disabled>⏳ Carregando categorias...</option>
                        ) : categoriasDisponiveis.length > 0 ? (
                            categoriasDisponiveis.map(categoria => (
                                <option key={getCategoriaId(categoria)} value={getCategoriaId(categoria)}>
                                    {getNomeFormatado(categoria)}
                                </option>
                            ))
                        ) : (
                            <option value="" disabled>
                                📝 Nenhuma categoria disponível do tipo "{formData.tipo}"
                            </option>
                        )}
                    </select>
                    
                    <div className="form-hint">
                        {errorCategorias ? (
                            <span className="text-red-500">Erro: {errorCategorias.message}</span>
                        ) : isLoadingCategorias ? (
                            'Carregando categorias...' 
                        ) : categoriasDisponiveis.length > 0 ? (
                            `Selecione uma categoria pai para criar uma subcategoria (${categoriasDisponiveis.length} disponíveis do tipo ${formData.tipo})` 
                        ) : (
                            `Não há categorias ativas disponíveis do tipo "${formData.tipo}" para serem pais`
                        )}
                    </div>
                </div>
            </div>
            )}

            {currentStep === 3 && (
            <div className="form-section">
                <h4 className="form-section-title">Imagem da Categoria</h4>

                <div className="flex flex-col gap-4">
                    {(imagemPreview || imagemUrl) && (
                        <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
                            <img 
                                src={imagemPreview || imagemUrl} 
                                alt="Preview" 
                                className="w-32 h-32 rounded-lg object-cover border shadow-sm"
                            />
                            <div className="flex-1">
                                <p className="text-sm text-gray-600 mb-2">
                                    Preview da imagem atual
                                </p>
                                {isEditing && (
                                    <button
                                        type="button"
                                        onClick={handleRemoverImagem}
                                        disabled={uploadLoading}
                                        className="action-button action-button-danger text-sm"
                                    >
                                        {uploadLoading ? <Loader2 size={16} className="animate-spin"/> : <Trash2 size={16} />}
                                        Remover Imagem
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                    
                    <div className="form-group">
                        <label className="file-input-label">
                            <Upload size={18} />
                            {imagemFile ? 'Alterar Imagem' : 'Selecionar Imagem'}
                            <input 
                                type="file" 
                                accept="image/*"
                                onChange={handleImagemChange}
                                className="file-input"
                            />
                        </label>
                        <div className="form-hint">
                            Formatos: JPG, PNG, WebP (máx. 5MB). Recomendado: 400x400px
                        </div>
                    </div>
                </div>
            </div>
            )}

            {currentStep === 4 && (
            <div className="form-section">
                <h4 className="form-section-title">
                    <Tag size={18} />
                    Tags e Metadados
                </h4>

                <div className="form-group">
                    <label htmlFor="novaTag">Tags</label>
                    <div className="flex gap-2">
                        <input 
                            type="text" 
                            id="novaTag"
                            value={novaTag}
                            onChange={(e) => setNovaTag(e.target.value)}
                            onKeyPress={handleKeyPress}
                            className="form-input flex-1"
                            placeholder="Digite uma tag e pressione Enter"
                            maxLength="50"
                        />
                        <button
                            type="button"
                            onClick={adicionarTag}
                            className="action-button action-button-secondary"
                        >
                            Adicionar
                        </button>
                    </div>
                    
                    {formData.metadata.tags.length > 0 && (
                        <div className="mt-3">
                            <div className="flex flex-wrap gap-2">
                                {formData.metadata.tags.map((tag, index) => (
                                    <span 
                                        key={index}
                                        className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                                    >
                                        {tag}
                                        <button
                                            type="button"
                                            onClick={() => removerTag(tag)}
                                            className="text-blue-600 hover:text-blue-800"
                                        >
                                            ×
                                        </button>
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                    <div className="form-hint">
                        Tags ajudam na organização e busca das categorias
                    </div>
                </div>
            </div>
            )}

            {currentStep === 5 && (
            <div className="form-section">
                <h4 className="form-section-title">Configurações</h4>

                <div className="form-grid-container">
                    <div className="form-group">
                        <label className="checkbox-label">
                            <input 
                                type="checkbox" 
                                name="ativo" 
                                checked={formData.ativo} 
                                onChange={onChange} 
                                className="checkbox-input"
                            />
                            <span className="checkbox-custom"></span>
                            <div>
                                <div className="font-medium">Categoria Ativa</div>
                                <div className="text-sm text-gray-500">
                                    Categoria visível no sistema
                                </div>
                            </div>
                        </label>
                    </div>

                    <div className="form-group">
                        <label className="checkbox-label">
                            <input 
                                type="checkbox" 
                                name="visivelParaCliente" 
                                checked={formData.visivelParaCliente} 
                                onChange={onChange} 
                                className="checkbox-input"
                            />
                            <span className="checkbox-custom"></span>
                            <div>
                                <div className="font-medium">Visível para Clientes</div>
                                <div className="text-sm text-gray-500">
                                    Exibir para clientes finais
                                </div>
                            </div>
                        </label>
                    </div>
                </div>
            </div>
            )}

            {showHeader && (
                <div className="form-actions sticky bottom-0 bg-white border-t pt-4">
                    <button
                        type="button"
                        onClick={onCloseForm}
                        className="action-button action-button-secondary"
                    >
                        <XCircle size={18} />
                        Cancelar
                    </button>
                    <FormStepNavigation
                        currentStep={currentStep}
                        totalSteps={STEP_LABELS.length}
                        onBack={() => setCurrentStep((s) => s - 1)}
                        onNext={() => setCurrentStep((s) => s + 1)}
                        isSubmitting={loading || uploadLoading}
                        submitLabel={isEditing ? 'Salvar Alterações' : 'Criar Categoria'}
                    />
                </div>
            )}
        </form>
    );
};

export default CategoriaForm;